import React, { Fragment, useEffect, useRef, useState } from "react";
import DropDownIcon from "../../assets/svg/DropdownIcon";
import ArrowUp from "../../assets/svg/ArrowUp";
import CustomImage from "./CustomImage";
import CustomText from "../atomic/CustomText";
import RequiredFieldTooltip from "./RequiredFieldTooltip";
import ContextItemLoadingIndicator from "./LoadingIndicators/ContextItemLoadingIndicator";

interface ContextSelectorProps {
  value: string | number;
  options: {
    label: string;
    value: string | number;
    image: string;
    address?: string;
    zipcode?: string;
    state?: string;
  }[];
  handleOptionClick: (value: string | number) => void;
  disabled: boolean;
  isLoading: boolean;
  defaultLabel: string;
  isCOA?: boolean;
  tooltipId: string;
  tooltipText: string;
}

const ContextSelector: React.FC<ContextSelectorProps> = ({
  options,
  value,
  handleOptionClick,
  disabled,
  isLoading,
  defaultLabel,
  isCOA,
  tooltipId,
  tooltipText,
}) => {
  const dropDownRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectedOption = options.find((option) => option.value === value);
  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setIsOpen(false); // Close dropdown if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (selectedValue: string | number) => {
    handleOptionClick(selectedValue);
    setSearchText(""); // Clear search text after selecting an option
    setIsOpen(false); // Close dropdown after selecting an option
  };

  return isLoading ? (
    <ContextItemLoadingIndicator />
  ) : (
    <RequiredFieldTooltip
      id={tooltipId}
      tooltipText={tooltipText}
      required={disabled}
    >
      <div
        className={`relative inline-block w-full text-left ${
          isCOA ? "lg:mr-5 mr-2" : "xl:mr-5 mr-2"
        }`}
        ref={dropDownRef}
      >
        <button
          className={`w-full whitespace-nowrap flex px-2 md:px-3 tab:max-w-[15vw] justify-between items-center rounded-md border shadow-sm py-1 bg-white text-sm font-medium text-gray-700 hover:bg-lightBlue border-primary ${
            disabled ? "cursor-not-allowed" : ""
          }`}
          onClick={() => setIsOpen((prevState) => !prevState)}
          disabled={disabled}
        >
          <div className="flex items-center">
            {selectedOption?.image && (
              <CustomImage
                src={selectedOption.image}
                className="h-3 w-5 md:mr-3 mr-1 object-fit"
                alt={`${selectedOption.label} image`}
              />
            )}
            <input
              placeholder={selectedOption?.label ?? defaultLabel}
              className={`whitespace-nowrap overflow-hidden overflow-ellipsis w-full placeholder:text-royalBlue font-normal focus:outline-none bg-inherit text-xs context-input`}
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
            {/* <CustomText
                text={selectedOption?.label ?? defaultLabel}
                fontSize="text-xs"
                fontWeight="font-normal"
                className="whitespace-nowrap overflow-hidden overflow-ellipsis 2xl:max-w-[140px] xl:max-w-[110px] lg:max-w-[80px]"
              /> */}
          </div>
          <div>{isOpen ? <ArrowUp /> : <DropDownIcon />}</div>
        </button>
        {isOpen && (
          <div className="py-1 cursor-pointer overflow-y-auto origin-top-left absolute left-0 mt-1 max-h-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[99999] max-w-[128%] md:min-w-[300px] md:max-w-[330px]">
            {options
              .filter((itemData) => {
                if (searchText === "") {
                  return itemData;
                }

                if (
                  itemData.label
                    .toLowerCase()
                    .includes(searchText.toLowerCase())
                ) {
                  return itemData;
                }
              })
              .map((itemData, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(itemData.value)}
                  className="px-2 md:px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
                >
                  <div className="flex items-center space-x-3">
                    <CustomImage
                      src={itemData.image}
                      className="h-3 w-5 object-fit mt-1 min-w-5"
                      alt={`${itemData.label} image`}
                    />
                    <div className="flex flex-col">
                      <CustomText
                        text={itemData.label}
                        fontWeight="font-normal"
                        fontSize="text-sm"
                        className="text-xs md:text-sm"
                      />
                      {[
                        itemData.address,
                        itemData.state,
                        itemData.zipcode,
                      ].some((val) => val && val.trim() !== "") && (
                        <CustomText
                          text={[
                            itemData.address,
                            itemData.state,
                            itemData.zipcode,
                          ]
                            .filter((val) => val && val.trim() !== "")
                            .join(", ")}
                          fontWeight="font-normal"
                          fontSize="text-xs"
                          className={`text-gray-500 mt-0.5`}
                        />
                      )}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>
    </RequiredFieldTooltip>
  );
};

export default ContextSelector;
